import React, { useState } from "react";
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useTheme,
    useMediaQuery,
    Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function Navbar() {
    const { logout, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const getDashboardPath = () => {
        if (!user) return "/";
        if (user.role === "super_admin") return "/super-admin/dashboard";
        if (user.role === "admin") return "/admin/dashboard";
        return "/candidate/dashboard";
    };

    const baseMenuItems = [
        { text: "Home", path: "/" },
        { text: "About", path: "/about" },
        { text: "Pricing", path: "/pricing" },
        { text: "Jobs", path: "/jobs" },
        { text: "Contact", path: "/contact" },
    ];

    // ✅ UPDATED LOGIC: Hide 'Pricing' for candidates, 'Jobs' for recruiters (admin)
    // AND Handle contextual Dashboard visibility
    const menuItems = [...baseMenuItems].filter(item => {
        if (!isAuthenticated) {
            // Issue 9: Hide 'Jobs' before login
            return item.text !== "Jobs";
        }

        // Hide Pricing for candidates after login
        if (user?.role === "candidate" && item.text === "Pricing") {
            return false;
        }

        // Hide Jobs for recruiters (admin) after login
        if (user?.role === "admin" && item.text === "Jobs") {
            return false;
        }

        // Hide Home and About for logged-in users to match required header structure
        if (item.text === "Home" || item.text === "About") {
            return false;
        }

        // Issue 10: Show Pricing, Contact, and JOBS after login (if not filtered above)
        return item.text === "Pricing" || item.text === "Contact" || item.text === "Jobs";
    });

    if (isAuthenticated) {
        const dashboardPath = getDashboardPath();
        if (location.pathname !== dashboardPath) {
            menuItems.unshift({ text: "Dashboard", path: dashboardPath });
        }
    }

    const drawer = (
        <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                <img src="/logo.png" alt="ExiHire Logo" style={{ height: 50 }} />
            </Box>

            <List>
                {menuItems.map((item) => (
                    <ListItem
                        key={item.text}
                        component={RouterLink}
                        to={item.path}
                        sx={{ color: "inherit", textDecoration: "none", textAlign: "center", display: "block", py: 1.5 }}
                    >
                        <ListItemText primary={item.text} />
                    </ListItem>
                ))}

                {isAuthenticated ? (
                    <>

                        <ListItem onClick={handleLogout} sx={{ color: "inherit", textDecoration: "none", textAlign: "center", display: "block", py: 1.5, cursor: "pointer" }}>
                            <ListItemText primary="Logout" />
                        </ListItem>
                    </>
                ) : (
                    <ListItem component={RouterLink} to="/login" sx={{ color: "inherit", textDecoration: "none", textAlign: "center", display: "block", py: 1.5 }}>
                        <ListItemText primary="Login" />
                    </ListItem>
                )}
            </List>
        </Box>
    );

    return (
        <>
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, bgcolor: '#0A0F2C', boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { md: "none" } }}>
                            <MenuIcon />
                        </IconButton>

                        <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'white' }}>
                            <img src="/logo.png" alt="ExiHire Logo" style={{height: isMobile ? 45 : 60, marginRight: 13 }} />
                        </Box>
                    </Box>

                    <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3, alignItems: 'center' }}>
                        {menuItems.map((item) => (
                            <Button key={item.text} color="inherit" component={RouterLink} to={item.path} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.95rem' }}>
                                {item.text}
                            </Button>
                        ))}

                        {isAuthenticated ? (
                            <Button
                                color="inherit"
                                onClick={handleLogout}
                                startIcon={<LogoutIcon />}
                                sx={{
                                    border: '1px solid rgba(255,255,255,0.5)',
                                    borderRadius: '8px',
                                    px: 2,
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        borderColor: 'white'
                                    }
                                }}
                            >
                                Logout
                            </Button>
                        ) : (
                            <Button
                                sx={{ backgroundColor: "white", color: 'primary.main', borderRadius: '8px', px: 3, textTransform: 'none', fontWeight: 600, ":hover": { backgroundColor: "#f0ffff" } }}
                                component={RouterLink}
                                to="/login"
                            >
                                Login
                            </Button>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            <Box component="nav">
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{ display: { xs: "block", md: "none" }, "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 } }}
                >
                    {drawer}
                </Drawer>
            </Box>
        </>
    );
}

export default Navbar;
